import { Ability } from '@recruitee/abilities-types';
import type { User } from '@recruitee/account-types';
import type { Candidate } from '@recruitee/candidate-types';
import type { Placement } from '@recruitee/placements-types';

function getOfferAllowedAdmins(admins: User[], offerId: number | undefined): User[] {
  if (!offerId) {
    return [];
  }

  return admins?.filter(admin => admin?.viewAllJobs || admin.offerIds?.includes(offerId)) ?? [];
}

function filterAdminsWhoCanSeePlacements(placementIds: number[], admins: User[]): User[] {
  return (
    admins?.filter((admin: User) => {
      const offerIdsPair: number[][] = [placementIds, (admin && admin.offerIds) || []];
      const placementIdsAdminCanSee: number[] = offerIdsPair.reduce((placementIds, adminOfferIds) =>
        placementIds.filter(placementId => adminOfferIds.includes(placementId)),
      );
      return admin.viewAllJobs || placementIdsAdminCanSee.length > 0;
    }) ?? []
  );
}

function filerAdminsWhoCanSeeHires(admins: User[], candidate: Candidate): User[] {
  const isHired = candidate?.placements.length
    ? candidate.placements.some(placement => placement.hiredAt)
    : false;

  return isHired
    ? (admins?.filter(admin =>
        admin.roleAbilities.some(ability => ability === Ability.ViewHiredCandidates),
      ) ?? [])
    : admins;
}

export function getOfferEvaluationAllowedAdmins(
  offerId: number,
  admins: User[],
  currentUserId?: number,
): User[] {
  if (!offerId) {
    return admins;
  }

  const allowedUsers: User[] = getOfferAllowedAdmins(admins, offerId);
  return currentUserId ? allowedUsers?.filter(user => user.id !== currentUserId) : allowedUsers;
}

export function getAllowedAdmins(data: {
  candidate: Candidate | undefined;
  admins: User[];
  offerId?: number;
  filterAdminsWithoutHiresAbility?: boolean;
}): User[] {
  if (!data.candidate?.placements) {
    return data.admins;
  } else if (!data.candidate.placements.length) {
    return (data.admins || []).filter(admin => admin.viewAllJobs);
  } else {
    const offerAllowedAdmins = getOfferAllowedAdmins(data.admins, data.offerId);

    if (offerAllowedAdmins.length) {
      return offerAllowedAdmins;
    }

    const placementIds = data.candidate.placements.map(
      (placement: Placement) => placement.offerId || placement.talentPoolId!,
    );
    const filteredAdmins = filterAdminsWhoCanSeePlacements(placementIds, data.admins);
    return data.filterAdminsWithoutHiresAbility
      ? filerAdminsWhoCanSeeHires(filteredAdmins, data.candidate)
      : filteredAdmins;
  }
}
